/**
 * QR Scanner Modal Component với API Integration
 * 
 * ✅ FULLY INTEGRATED!
 * Package @yudiel/react-qr-scanner đã được cài đặt và kích hoạt
 * API send-special đã được tích hợp theo FCO.b2s-2025-react pattern
 * 
 * FEATURES:
 * - Real-time QR code scanning
 * - Camera access với back camera priority
 * - Sound feedback khi scan thành công
 * - Error handling cho camera permissions
 * - Flash/torch control
 * - Responsive design
 * - API Integration với send-special endpoint
 * - Loading state management
 * - Auto-close sau khi thành công
 * 
 * API: POST /api/user/send-special { secret: qrData }
 */

import React, { useState, useRef } from 'react';
import Modal from 'react-modal';
// ✅ Package đã được cài đặt
import { Scanner } from '@yudiel/react-qr-scanner';
import type { IDetectedBarcode } from '@yudiel/react-qr-scanner';
import styles from './ModalVerifyQr.module.scss';

// API imports theo pattern
import { getUserInfo } from '@/models/User';
import { updateCache } from '@/store/action';
import { showMessage, login } from '@/lib';
import { $api } from '@/queryClient';
import { useStore } from '@/store';
import config from '@/config';
import lang from '@/lib/lng';

Modal.setAppElement('#root');

// Setup language theo pattern
const lng: { [key: string]: string } = lang[config?.lng];

type Props = {
 modalStatus: boolean
 closeModal: () => void
}

const ModalVerifyQr: React.FC<Props> = ({ modalStatus, closeModal }) => {
 const [isScanning, setIsScanning] = useState<boolean>(true);
 const [error, setError] = useState<string>('');
 // const [dataQr, setDataQr] = useState<any>(null);
 const scannerRef = useRef<HTMLDivElement>(null);

 // API setup theo pattern
 const { mutate: sendSpecial } = $api.useMutation('post', '/api/user/verify-special');
 const userInfo = getUserInfo();
 const { updateState, isCallingApi } = useStore();

 const afterOpenModal = () => {
  // Reset state when modal opens
  setIsScanning(true);
  setError('');
 };

 const selfClose = (event: React.SyntheticEvent) => {
  event.preventDefault();
  const modalElement = document.querySelector('.swing-in-top-fwd');
  if (modalElement) {
   modalElement.classList.add('swing-out-top-bck');
  }

  setTimeout(() => {
   closeModal();
  }, 300);
 };

 // Handle QR code scan result
 const handleScan = (detectedCodes: IDetectedBarcode[]) => {
  if (detectedCodes && detectedCodes.length > 0) {
   const qrData = detectedCodes[0].rawValue;
   setIsScanning(false);
   setError('');
   // setDataQr(qrData);
   
   // Log the scanned data
   // console.log('QR Code scanned:', qrData);
   
   // You can add your verification logic here
   handleVerification(qrData);
  }
 };

 // Handle scan errors
 const handleError = (error: unknown) => {
  console.error('QR Scanner Error:', error);
  setError('Không thể truy cập camera. Vui lòng kiểm tra quyền camera.');
 };

 // Handle QR verification logic với API integration
 const handleVerification = (qrData: string) => {
  // Step 1: Event validation
  if (!userInfo?.payload?.event_started) {
   showMessage(config.eventMsg);
   return false;
  }

  // Step 2: User authentication
  if (!userInfo?.payload?.user?.id) {
   login();
   return false;
  }

  // Step 3: Prevent multiple calls
  if (isCallingApi) {
   return false;
  }

  // Step 4: Set loading state
  updateState('isCallingApi', true);

  // Step 5: Execute API call
  sendSpecial(
   { 
    body: {
     secret: qrData // QR data làm secret
    }
   },
   {
    onSuccess: (data: any) => {
     if (data?.status === 'successful') {
      const oldCache = { ...userInfo };
      
      showMessage('Xác minh QR thành công!').then(() => {
       updateCache(['get', '/api/user/get', {}], null, {
        ...oldCache,
        payload: {
         ...oldCache?.payload,
         user: {
          ...oldCache?.payload?.user,
          // Update user data từ response nếu có
          is_student: data?.payload?.user?.is_student
         },
        },
       });
       updateState('isCallingApi', false);
       restartScan();
       
       // Close modal sau khi thành công
       // setTimeout(() => {
       //  selfClose({ preventDefault: () => {} } as React.SyntheticEvent);
       // }, 2000);
      });
     } else {
      const errCode: string = (data as any)?.error_code ?? 'defaultError';
      showMessage(
       lng[errCode] ? lng[errCode] : `${lng.defaultError} [${errCode}]`
      ).then(() => {
       updateState('isCallingApi', false);
       // Restart scan on error
       restartScan();
      });
     }
    },
    onError: () => {
     showMessage('Lỗi kết nối. Vui lòng thử lại.').then(() => {
      updateState('isCallingApi', false);
      restartScan();
     });
    },
   }
  );
 };

 // Restart scanning
 const restartScan = () => {
  setIsScanning(true);
  setError('');
 };

 return (
  <Modal
   isOpen={modalStatus}
   onAfterOpen={afterOpenModal}
   onRequestClose={selfClose}
   contentLabel="QR Code Scanner Modal"
   portalClassName="ReactModalPortal"
   overlayClassName=""
   className="animated fadeInDown faster modal-verify-qr"
  >
   <div className="modal-description">
    <h2>Xác minh sinh viên bằng QR code</h2>
    {/* <div className={styles['data-qr']}>
     <p>{dataQr}</p>
    </div> */}
    <div className={styles['verify-qr']} ref={scannerRef}>
     {isCallingApi ? (
      <div className={styles['loading-spinner']}>
       <div className={styles['spinner']}></div>
       <p>Đang xác minh QR code...</p>
      </div>
     ) : isScanning && (
      <div className={styles['scanner-container']}>
       <Scanner
        onScan={handleScan}
        onError={handleError}
        constraints={{
         width: { min: 640, ideal: 1280, max: 1920 },
         height: { min: 480, ideal: 720, max: 1080 },
         facingMode: 'environment' // Use back camera
        }}
        formats={['qr_code']} // Only scan QR codes
        components={{
         finder: true,
         torch: true,
         zoom: false
        }}
        styles={{
         container: { 
          width: '100%', 
          maxWidth: '20.833vw',
          margin: '0 auto',
          borderRadius: '0.417vw',
          overflow: 'hidden'
         },
         video: { 
          width: '100%',
          height: 'auto'
         }
        }}
        scanDelay={100}
        allowMultiple={false}
        sound={true}
       />
       
       <div className={styles['scanner-overlay']}>
        <p className="text-center">Hướng camera vào mã QR để quét</p>
       </div>
      </div>
     )}
     
     {error && (
      <div className={`text-center ${styles['error-message']}`}>
       <p>⚠️ {error}</p>
       <button onClick={restartScan} className='btn'>Thử lại</button>
      </div>
     )}
    </div>
   </div>
  </Modal>
 )
}

export default ModalVerifyQr